import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Group,
  MultiSelect,
  NumberInput,
  ScrollArea,
  SegmentedControl,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { type Tack, type TackStats, type TrackPoint } from '../../types';
import { formatMetricLabel, formatMetricLabelNoUnit } from '../../utils/metricLabels';
import { calcularEstadisticasTack } from '../../utils/tack';
import {
  getManeuverLabels,
  subscribeManeuverLabels,
  type ManeuverLabelMap,
} from '../../state/maneuverVisibilityStore';

import './ManeuversPanel.css';
import './themeInversion.css';

type ManeuverType = 'tack' | 'gybe';

type WindowSample = {
  offsetSeconds: number;
  point: TrackPoint;
  index: number;
};

type ManeuverSummary = {
  id: string;
  tackIndex: number;
  displayIndex: number;
  label: string;
  type: ManeuverType;
  startUtc: string;
  startTimeMs: number | null;
  durationSeconds: number | null;
  tws: number | null;
  twa: number | null;
  preBsp: number | null;
  postBsp: number | null;
  preVmg: number | null;
  postVmg: number | null;
  preVmgPc: number | null;
  postVmgPc: number | null;
  maxRudder: number | null;
  timeToMaxRudder: number | null;
  preSog: number | null;
  preTwa: number | null;
  speedLoss: number | null;
  minSog: number | null;
  timeToRecovery90: number | null;
  timeToRecovery95: number | null;
  samples: WindowSample[];
  color: string;
};

const RESERVED_METRICS = new Set(['utc', 'lat', 'lon', 'globalIndex']);
const COLOR_PALETTE = [
  '#4ECDC4',
  '#E06C75',
  '#7C3AED',
  '#F6C344',
  '#45A3E5',
  '#F28482',
  '#2EC4B6',
  '#6E67FF',
] as const;

const clampSeconds = (value: number, fallback: number): number => {
  if (!Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(Math.max(value, 5), 120);
};

const coerceNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const averageMetric = (samples: Array<{ point: TrackPoint }>, metric: string): number | null => {
  const values = samples
    .map((sample) => coerceNumber((sample.point as unknown as Record<string, unknown>)[metric]))
    .filter((value): value is number => value !== null);
  if (values.length === 0) {
    return null;
  }
  const sum = values.reduce((acc, value) => acc + value, 0);
  return sum / values.length;
};

const formatTime = (utc: string) => {
  const date = new Date(utc);
  if (Number.isNaN(date.getTime())) {
    return '–';
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const translateIndex = (
  index: number | undefined,
  map: Record<number, number>,
  trackLength: number,
): number | null => {
  if (index === undefined || index === null) {
    return null;
  }
  const mapped = map[index];
  const candidate = mapped ?? index;
  if (candidate < 0 || candidate >= trackLength) {
    return null;
  }
  return candidate;
};

const collectWindowSeries = (
  trackData: TrackPoint[],
  startIndex: number,
  secondsBefore: number,
  secondsAfter: number,
): WindowSample[] => {
  const startPoint = trackData[startIndex];
  if (!startPoint?.utc) {
    return [];
  }
  const startTimeMs = Date.parse(startPoint.utc);
  if (!Number.isFinite(startTimeMs)) {
    return [];
  }
  const minTime = startTimeMs - secondsBefore * 1000;
  const maxTime = startTimeMs + secondsAfter * 1000;

  const samples: WindowSample[] = [];

  // Walk backwards from the start to capture the pre-window quickly
  for (let index = startIndex; index >= 0; index -= 1) {
    const utc = trackData[index]?.utc;
    const ts = utc ? Date.parse(utc) : Number.NaN;
    if (!Number.isFinite(ts) || ts < minTime) {
      break;
    }
    samples.push({ offsetSeconds: (ts - startTimeMs) / 1000, point: trackData[index], index });
  }

  // Walk forwards from startIndex
  for (let index = startIndex + 1; index < trackData.length; index += 1) {
    const utc = trackData[index]?.utc;
    const ts = utc ? Date.parse(utc) : Number.NaN;
    if (!Number.isFinite(ts) || ts > maxTime) {
      break;
    }
    samples.push({ offsetSeconds: (ts - startTimeMs) / 1000, point: trackData[index], index });
  }

  return samples.sort((a, b) => a.offsetSeconds - b.offsetSeconds);
};

const bucketOffset = (value: number): number => Math.round(value * 2) / 2;

const getTwaValue = (point: TrackPoint): number | null => {
  const twaAbs = coerceNumber(point.twa_abs);
  if (twaAbs !== null) {
    return twaAbs;
  }
  return coerceNumber(point.twa);
};

const findPeakTwaGradientIndex = (
  trackData: TrackPoint[],
  startIndex: number,
  endIndex: number,
): number => {
  let bestIndex = startIndex;
  let bestDiff = -Infinity;

  for (let index = Math.max(startIndex + 1, 1); index <= endIndex; index += 1) {
    const previous = trackData[index - 1];
    const current = trackData[index];
    if (!previous || !current) {
      continue;
    }
    const prevTwa = getTwaValue(previous);
    const currTwa = getTwaValue(current);
    if (prevTwa === null || currTwa === null) {
      continue;
    }
    const diff = Math.abs(currTwa - prevTwa);
    if (diff > bestDiff) {
      bestDiff = diff;
      bestIndex = index;
    }
  }

  return bestIndex;
};

const formatOffsetLabel = (value: number): string => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}s`;
};

const formatNumber = (value: number | null, digits = 1): string =>
  value === null || Number.isNaN(value) ? '–' : value.toFixed(digits);

export interface ManeuversPanelProperties {
  trackData: TrackPoint[];
  tacks: Tack[];
  globalToRelativeIndexMap: Record<number, number>;
  maneuverVisibility?: Record<number, boolean>;
  onManeuverVisibilityChange?: (tackIndex: number, visible: boolean) => void;
}

export const ManeuversPanel: React.FC<ManeuversPanelProperties> = ({
  trackData,
  tacks,
  globalToRelativeIndexMap,
  maneuverVisibility,
  onManeuverVisibilityChange,
}) => {
  const { t } = useTranslation('common');
  const [typeFilter, setTypeFilter] = useState<'all' | ManeuverType>('all');
  const [secondsBefore, setSecondsBefore] = useState<number>(30);
  const [secondsAfter, setSecondsAfter] = useState<number>(50);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['bsp', 'vmg', 'twa_abs']);
  const [visibleManeuvers, setVisibleManeuvers] = useState<Record<string, boolean>>({});
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [maneuverLabels, setManeuverLabels] = useState<ManeuverLabelMap>({});

  const availableMetrics = useMemo(() => {
    const set = new Set<string>();
    for (const point of trackData) {
      for (const [key, raw] of Object.entries(point)) {
        if (RESERVED_METRICS.has(key)) {
          continue;
        }
        if (coerceNumber(raw) !== null) {
          set.add(key);
        }
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [trackData]);

  useEffect(() => {
    setSelectedMetrics((previous) => {
      const valid = previous.filter((metric) => availableMetrics.includes(metric));
      if (valid.length > 0) {
        return valid;
      }
      return availableMetrics.slice(0, 3);
    });
  }, [availableMetrics]);

  const twaKey = useMemo(() => {
    if (trackData.some((point) => typeof point.twa_abs === 'number')) {
      return 'twa_abs';
    }
    if (trackData.some((point) => typeof point.twa === 'number')) {
      return 'twa';
    }
    return null;
  }, [trackData]);

  const maneuvers = useMemo<ManeuverSummary[]>(() => {
    if (trackData.length === 0 || tacks.length === 0) {
      return [];
    }
    const safeBefore = clampSeconds(secondsBefore, 30);
    const safeAfter = clampSeconds(secondsAfter, 50);

    return tacks
      .map((tack, index) => {
        const startAbs =
          (tack as Partial<{ start_index: number; startIndex: number }>).start_index ??
          (tack as Partial<{ start_index: number; startIndex: number }>).startIndex;
        const endAbs =
          (tack as Partial<{ end_index: number; endIndex: number }>).end_index ??
          (tack as Partial<{ end_index: number; endIndex: number }>).endIndex;

        const startIndex = translateIndex(startAbs, globalToRelativeIndexMap, trackData.length);
        const endIndex = translateIndex(endAbs, globalToRelativeIndexMap, trackData.length);
        if (startIndex === null || endIndex === null) {
          return null;
        }
        const [start, end] =
          startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
        const anchorIndex = findPeakTwaGradientIndex(trackData, start, end);
        const startPoint = trackData[anchorIndex];
        const endPoint = trackData[end] ?? trackData[start];
        if (!startPoint?.utc) {
          return null;
        }

        const startTimeMs = Date.parse(startPoint.utc);
        const endTimeMs = endPoint?.utc ? Date.parse(endPoint.utc) : Number.NaN;
        const durationSeconds =
          Number.isFinite(startTimeMs) && Number.isFinite(endTimeMs)
            ? Number(((endTimeMs - startTimeMs) / 1000).toFixed(1))
            : null;

        const segment = trackData.slice(start, end + 1);
        const stats = calcularEstadisticasTack(segment, trackData);
        const samples = collectWindowSeries(trackData, anchorIndex, safeBefore, safeAfter);

        const preSamples = samples.filter((sample) => sample.offsetSeconds < 0);
        const postSamples = samples.filter((sample) => sample.offsetSeconds >= 0);

        const preBsp = averageMetric(preSamples, 'bsp');
        const postBsp = averageMetric(postSamples, 'bsp');
        const preVmg = averageMetric(preSamples, 'vmg');
        const postVmg = averageMetric(postSamples, 'vmg');
        const preVmgPc = averageMetric(preSamples, 'vmgpc');
        const postVmgPc = averageMetric(postSamples, 'vmgpc');
        const preSog = averageMetric(preSamples, 'sog');
        const preTwa = twaKey ? averageMetric(preSamples, twaKey) : null;

        const selectedTwa = twaKey ? averageMetric(samples, twaKey) : null;
        const averageTws = averageMetric(samples, 'tws');

        const speedLoss =
          stats.SogMin !== undefined && stats.SogMin !== null && preSog !== null
            ? Number((preSog - stats.SogMin).toFixed(1))
            : null;

        const type: ManeuverType = stats.type ?? 'tack';
        const id = String(tack.id ?? `${start}-${end}-${index}`);
        const label = `${
          type === 'gybe'
            ? t('race.maneuvers.labels.gybe', 'Gybe')
            : t('race.maneuvers.labels.tack', 'Tack')
        } ${index + 1}`;

        return {
          id,
          tackIndex: index,
          displayIndex: index + 1,
          label,
          type,
          startUtc: startPoint.utc,
          startTimeMs: Number.isFinite(startTimeMs) ? startTimeMs : null,
          durationSeconds,
          tws: averageTws,
          twa: selectedTwa,
          preBsp,
          postBsp,
          preVmg,
          postVmg,
          preVmgPc,
          postVmgPc,
          maxRudder: stats.maxRudder ?? null,
          timeToMaxRudder: stats.timeToMaxRudder ?? null,
          preSog,
          preTwa,
          speedLoss,
          minSog: stats.SogMin ?? null,
          timeToRecovery90: stats.timeToRecovery90 ?? null,
          timeToRecovery95: stats.timeToRecovery95 ?? null,
          samples,
          color: COLOR_PALETTE[index % COLOR_PALETTE.length],
        } as ManeuverSummary;
      })
      .filter((value): value is ManeuverSummary => value !== null)
      .sort((a, b) => (a.startTimeMs ?? 0) - (b.startTimeMs ?? 0));
  }, [globalToRelativeIndexMap, secondsAfter, secondsBefore, t, tacks, trackData, twaKey]);

  useEffect(() => {
    setVisibleManeuvers((previous) => {
      const next = { ...previous };
      for (const maneuver of maneuvers) {
        if (next[maneuver.id] === undefined) {
          next[maneuver.id] = false; // default: hidden until user toggles
        }
      }
      return next;
    });
  }, [maneuvers]);

  useEffect(() => {
    if (maneuverVisibility !== undefined) {
      setVisibleManeuvers((previous) => {
        const next = { ...previous };
        for (const maneuver of maneuvers) {
          const external = maneuverVisibility[maneuver.tackIndex];
          if (external !== undefined) {
            next[maneuver.id] = external !== false;
          }
        }
        return next;
      });
    }
  }, [maneuverVisibility, maneuvers]);

  useEffect(() => {
    setManeuverLabels(getManeuverLabels());
    const unsubscribe = subscribeManeuverLabels(setManeuverLabels);
    return () => unsubscribe();
  }, []);

  const filteredManeuvers = useMemo(() => {
    if (typeFilter === 'all') {
      return maneuvers;
    }
    return maneuvers.filter((maneuver) => maneuver.type === typeFilter);
  }, [maneuvers, typeFilter]);

  const computedVisibility = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const maneuver of maneuvers) {
      const external = maneuverVisibility?.[maneuver.tackIndex];
      if (external === undefined) {
        map.set(maneuver.id, visibleManeuvers[maneuver.id] !== false);
      } else {
        map.set(maneuver.id, external !== false);
      }
    }
    return map;
  }, [maneuverVisibility, maneuvers, visibleManeuvers]);

  const enabledManeuvers = useMemo(
    () => filteredManeuvers.filter((maneuver) => computedVisibility.get(maneuver.id) !== false),
    [computedVisibility, filteredManeuvers],
  );

  const visibleOrderById = useMemo(() => {
    const map = new Map<string, number>();
    let counter = 1;
    for (const maneuver of filteredManeuvers) {
      if (computedVisibility.get(maneuver.id) !== false) {
        map.set(maneuver.id, counter);
        counter += 1;
      }
    }
    return map;
  }, [computedVisibility, filteredManeuvers]);

  useEffect(() => {
    if (focusedId && !enabledManeuvers.some((maneuver) => maneuver.id === focusedId)) {
      setFocusedId(null);
    }
  }, [enabledManeuvers, focusedId]);

  const chartDataByMetric = useMemo(() => {
    const result: Record<string, Array<Record<string, number>>> = {};
    for (const metric of selectedMetrics) {
      const buckets = new Map<number, Record<string, number>>();
      for (const maneuver of enabledManeuvers) {
        for (const sample of maneuver.samples) {
          const value = coerceNumber((sample.point as unknown as Record<string, unknown>)[metric]);
          if (value === null) {
            continue;
          }
          const bucket = bucketOffset(sample.offsetSeconds);
          const existing = buckets.get(bucket) ?? { time: bucket };
          existing[maneuver.id] = value;
          buckets.set(bucket, existing);
        }
      }
      const rows = [...buckets.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([time, data]) => ({ ...data, time }));
      result[metric] = rows;
    }
    return result;
  }, [enabledManeuvers, selectedMetrics]);

  const handleToggleAll = useCallback(
    (visible: boolean) => {
      setVisibleManeuvers((previous) => {
        const next = { ...previous };
        for (const maneuver of filteredManeuvers) {
          next[maneuver.id] = visible;
          onManeuverVisibilityChange?.(maneuver.tackIndex, visible);
        }
        return next;
      });
    },
    [filteredManeuvers, onManeuverVisibilityChange],
  );

  if (trackData.length === 0) {
    return (
      <div className="maneuvers-panel empty">
        <p>{t('race.maneuvers.emptyTrack', 'Load a file to see maneuvers.')}</p>
      </div>
    );
  }

  if (tacks.length === 0 || maneuvers.length === 0) {
    return (
      <div className="maneuvers-panel empty">
        <p>
          {t('race.maneuvers.empty', 'No maneuvers detected for this selection. Try another race.')}
        </p>
      </div>
    );
  }

  return (
    <div className="maneuvers-panel theme-inverted" data-mantine-color-scheme="light">
      <div className="maneuvers-header">
        <h3>{t('race.maneuvers.title', 'Maneuver Analysis')}</h3>
        <Button size="xs" variant="light" onClick={() => setSettingsOpen((open) => !open)}>
          {settingsOpen
            ? t('race.maneuvers.controls.hideSettings', 'Hide settings')
            : t('race.maneuvers.controls.showSettings', 'Show settings')}
        </Button>
      </div>

      {settingsOpen && (
        <div className="maneuvers-controls maneuvers-card">
          <div className="maneuvers-controls-grid">
            <div className="maneuvers-control" data-area="filter">
              <div className="maneuvers-control-label">
                {t('race.maneuvers.filters.all', 'Filter')}
              </div>
              <SegmentedControl
                fullWidth
                className="maneuvers-segmented"
                value={typeFilter}
                onChange={(value) => setTypeFilter(value as typeof typeFilter)}
                data={[
                  { value: 'all', label: t('race.maneuvers.filters.all', 'All') },
                  { value: 'tack', label: t('race.maneuvers.filters.tacks', 'Tacks') },
                  { value: 'gybe', label: t('race.maneuvers.filters.gybes', 'Gybes') },
                ]}
              />
            </div>

            <div className="maneuvers-control metrics" data-area="metrics">
              <div className="maneuvers-control-label">
                {t('race.maneuvers.filters.metrics', 'Line chart metrics')}
              </div>
              <MultiSelect
                className="maneuvers-metric-select"
                placeholder={t('race.maneuvers.filters.selectMetric', 'Select metrics')}
                data={availableMetrics.map((metric) => ({
                  value: metric,
                  label: formatMetricLabel(metric),
                }))}
                maxDropdownHeight={240}
                searchable
                value={selectedMetrics}
                onChange={setSelectedMetrics}
                nothingFoundMessage={t('race.maneuvers.filters.noMetrics', 'No numeric metrics')}
              />
            </div>

            <div className="maneuvers-control window" data-area="window">
              <div className="maneuvers-control-label">
                {t('race.maneuvers.filters.window', 'Time window')}
              </div>
              <div className="maneuvers-window-grid">
                <NumberInput
                  className="maneuvers-number-input"
                  label={t('race.maneuvers.filters.before', 'Seconds before')}
                  value={secondsBefore}
                  min={5}
                  max={120}
                  step={5}
                  onChange={(value) => setSecondsBefore(clampSeconds(Number(value), 30))}
                />
                <NumberInput
                  className="maneuvers-number-input"
                  label={t('race.maneuvers.filters.after', 'Seconds after')}
                  value={secondsAfter}
                  min={5}
                  max={120}
                  step={5}
                  onChange={(value) => setSecondsAfter(clampSeconds(Number(value), 50))}
                />
              </div>
            </div>

            <div className="maneuvers-actions" data-area="actions">
              <Button
                variant="outline"
                size="xs"
                className="maneuvers-ghost-btn"
                onClick={() => handleToggleAll(true)}
              >
                {t('race.maneuvers.controls.showAll', 'Show all')}
              </Button>
              <Button
                variant="filled"
                size="xs"
                className="maneuvers-ghost-btn"
                onClick={() => handleToggleAll(false)}
              >
                {t('race.maneuvers.controls.hideAll', 'Hide all')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="maneuvers-body">
        <div className="maneuvers-table-wrapper maneuvers-card">
          <ScrollArea type="auto">
            <table className="maneuvers-table">
              <thead>
                <tr>
                  <th />
                  <th>{t('race.maneuvers.table.maneuver', 'Maneuver')}</th>
                  <th>{t('race.maneuvers.table.time', 'Time')}</th>
                  <th>{t('race.maneuvers.table.duration', 'Duration (s)')}</th>
                  <th>{t('race.maneuvers.table.tws', 'TWS (kts)')}</th>
                  <th>{t('race.maneuvers.table.twa', 'TWA (°)')}</th>
                  <th>{t('race.maneuvers.table.bspBefore', 'BSP before')}</th>
                  <th>{t('race.maneuvers.table.bspAfter', 'BSP after')}</th>
                  <th>{t('race.maneuvers.table.vmgBefore', 'VMG before')}</th>
                  <th>{t('race.maneuvers.table.vmgAfter', 'VMG after')}</th>
                  <th>{t('race.maneuvers.table.vmgPcBefore', 'VMG% before')}</th>
                  <th>{t('race.maneuvers.table.vmgPcAfter', 'VMG% after')}</th>
                <th>{t('race.maneuvers.table.maxRudder', 'Max rudder (°)')}</th>
                <th>{t('race.maneuvers.table.timeToMaxRudder', 'Time to max rudder (s)')}</th>
                <th>{t('race.maneuvers.table.preSog', 'Pre SOG (kts)')}</th>
                <th>{t('race.maneuvers.table.preTwa', 'Pre TWA (°)')}</th>
                <th>{t('race.maneuvers.table.loss', 'Speed loss (kts)')}</th>
                <th>{t('race.maneuvers.table.timeToSog90', 'Time to SOG 90% (s)')}</th>
                <th>{t('race.maneuvers.table.timeToSog95', 'Time to SOG 95% (s)')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredManeuvers.map((maneuver) => {
                const isFocused = focusedId === maneuver.id;
                  const isVisible = computedVisibility.get(maneuver.id) !== false;
                  const labelValue = isVisible
                    ? maneuverLabels[maneuver.tackIndex] ??
                      visibleOrderById.get(maneuver.id) ??
                      maneuver.displayIndex
                    : undefined;
                  return (
                    <tr
                      key={maneuver.id}
                      className={`${isFocused ? 'focused' : ''} ${isVisible ? '' : 'muted'}`}
                      onClick={() =>
                        setFocusedId((previous) => (previous === maneuver.id ? null : maneuver.id))
                      }
                    >
                      <td>
                        <label className="maneuver-toggle">
                          <span className="visually-hidden">
                            {t('race.maneuvers.table.maneuver', 'Maneuver')} {maneuver.label}
                          </span>
                        <input
                          type="checkbox"
                          aria-label={maneuver.label}
                          checked={isVisible}
                          onChange={(event) => {
                            const checked = event.currentTarget?.checked ?? false;
                            setVisibleManeuvers((previous) => ({
                              ...previous,
                              [maneuver.id]: checked,
                            }));
                            onManeuverVisibilityChange?.(maneuver.tackIndex, checked);
                          }}
                        />
                          <span
                            className="maneuver-color"
                            style={{ backgroundColor: maneuver.color }}
                          />
                        </label>
                      </td>
                      <td>
                        <Group gap={6} wrap="nowrap">
                      <Badge
                        color={maneuver.type === 'gybe' ? 'orange' : 'blue'}
                        variant="light"
                        size="sm"
                        className="maneuver-index-badge"
                      >
                        <span className="maneuver-index-text">
                          {labelValue ?? maneuver.displayIndex}
                        </span>
                      </Badge>
                      <span>{maneuver.label}</span>
                    </Group>
                  </td>
                      <td>{formatTime(maneuver.startUtc)}</td>
                      <td>{formatNumber(maneuver.durationSeconds)}</td>
                      <td>{formatNumber(maneuver.tws)}</td>
                      <td>{formatNumber(maneuver.twa)}</td>
                      <td>{formatNumber(maneuver.preBsp)}</td>
                      <td>{formatNumber(maneuver.postBsp)}</td>
                      <td>{formatNumber(maneuver.preVmg)}</td>
                    <td>{formatNumber(maneuver.postVmg)}</td>
                    <td>{formatNumber(maneuver.preVmgPc)}</td>
                    <td>{formatNumber(maneuver.postVmgPc)}</td>
                    <td>{formatNumber(maneuver.maxRudder)}</td>
                    <td>{formatNumber(maneuver.timeToMaxRudder)}</td>
                    <td>{formatNumber(maneuver.preSog)}</td>
                    <td>{formatNumber(maneuver.preTwa)}</td>
                    <td>{formatNumber(maneuver.speedLoss)}</td>
                    <td>{formatNumber(maneuver.timeToRecovery90)}</td>
                    <td>{formatNumber(maneuver.timeToRecovery95)}</td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </ScrollArea>
        </div>

        <div className="maneuvers-chart maneuvers-card">
          <div className="chart-header">
            <div>
              <h4>{t('race.maneuvers.chart.title', 'Line chart metrics')}</h4>
              <p>
                {t('race.maneuvers.chart.hint', 'Lines are aligned to time zero (maneuver peak).')}
              </p>
            </div>
            <Badge variant="light">
              {t('race.maneuvers.chart.visibleCount', '{{count}} maneuvers', {
                count: enabledManeuvers.length,
              })}
            </Badge>
          </div>
          <div className="chart-scroll">
            {selectedMetrics.map((metric) => {
              const chartData = chartDataByMetric[metric] ?? [];
              if (chartData.length === 0) {
                return (
                  <div key={metric} className="chart-empty">
                    {t('race.maneuvers.chart.empty', 'No data for {{metric}}', {
                      metric: formatMetricLabelNoUnit(metric),
                    })}
                  </div>
                );
              }
              return (
                <div key={metric} className="chart-block">
                  <div className="chart-title">{formatMetricLabel(metric)}</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={chartData} margin={{ top: 8, bottom: 0, left: 0, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--borderSubtle)" />
                      <XAxis
                        dataKey="time"
                        stroke="var(--textMutedStrong)"
                        tickFormatter={formatOffsetLabel}
                        type="number"
                        domain={['auto', 'auto']}
                      />
                      <YAxis stroke="var(--textMutedStrong)" />
                      <Tooltip
                        formatter={(value: number, key: string) => {
                          const maneuver = enabledManeuvers.find((item) => item.id === key);
                          return [
                            Number.isFinite(value) ? value.toFixed(2) : value,
                            maneuver?.label ?? key,
                          ];
                        }}
                        labelFormatter={(value) => formatOffsetLabel(Number(value))}
                      />
                      <ReferenceLine x={0} stroke="var(--chart-1, #e11d48)" strokeDasharray="4 4" />
                      {enabledManeuvers.map((maneuver) => (
                        <Line
                          key={`${metric}-${maneuver.id}`}
                          type="monotone"
                          dataKey={maneuver.id}
                          stroke={maneuver.color}
                          strokeWidth={focusedId === maneuver.id ? 2.5 : 1.5}
                          dot={false}
                          isAnimationActive={false}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
